// Phase SIKA-5g — Secondary chat sidebar (Claude.ai-style).
//
// Sits between the AppShell global sidebar and the chat column.
// Always visible on desktop, collapses to a 52px icon rail.
// On mobile becomes an overlay drawer.
//
// Sections:
//   1. Brand row    — SIKA logo + collapse toggle
//   2. Top nav      — New conversation, Search (stub), Conversations,
//                     Goals (jumps to /sika), Active plan (jumps to
//                     /sika?goal=<active>), Vault (jumps to /vault),
//                     Pinned (stub)
//   3. Goals list   — active goals with progress meta
//   4. Recent       — time-grouped conversation list (via existing
//                     SikaHistoryRail content adapted inline)
//   5. User chip    — email + tier pill
//
// CSS lives in src/styles/super-acca.css under .sa-root
// .sika-chat-sidebar. Host (SikaChatPage.v2) renders inside
// .sika-chat-shell.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../../../components/icons/Icon.tsx";
import type { SikaGoalWithMessages } from "../../../sika/useSikaGoals.ts";
import { SikaHistoryRail } from "../SikaHistoryRail.tsx";

const TERMINAL_STATUSES = new Set(["completed", "archived", "cancelled"]);

export interface SikaChatSidebarProps {
  /** All goals from useSikaGoals (parent filters here). */
  goals: ReadonlyArray<SikaGoalWithMessages>;
  /** Currently selected goal in chat (drives goal row data-active). */
  activeGoalId?: string | null;
  /** Setter on the chat hook. */
  onSelectGoal: (goalId: string) => void;
  /** Start a fresh session. */
  onNewConversation: () => void;
  /** Active session id (drives Conversations row active state). */
  activeSessionId?: string | null;
  /** Load a previous session. */
  onSelectSession: (sessionId: string, goalId: string | null) => void;
  /** User chip — email + pro flag. */
  userEmail?: string | null;
  pro?: boolean;
  /** Initial collapsed state (persisted to localStorage by parent). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Mobile drawer open state. */
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return Math.round(n).toString();
  if (n < 100_000) {
    const k = n / 1000;
    return `${k.toFixed(1).replace(/\.0$/, "")}K`;
  }
  return `${Math.floor(n / 1000)}K`;
}

function goalMeta(g: SikaGoalWithMessages): string {
  const current = Number(g.current_amount ?? 0);
  const target = Number(g.target_amount ?? 0);
  if (target <= 0) return g.status ?? "—";
  const pct = Math.round((current / target) * 100);
  return `${formatCompact(current)}/${formatCompact(target)} · ${pct}%`;
}

export function SikaChatSidebar({
  goals,
  activeGoalId = null,
  onSelectGoal,
  onNewConversation,
  activeSessionId = null,
  onSelectSession,
  userEmail = null,
  pro = false,
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onCloseMobile,
}: SikaChatSidebarProps): JSX.Element {
  // Local UI state for which section is "active" in the top nav.
  // Selecting Goals/Vault/Active plan navigates AWAY via Link, so
  // their active state only renders briefly on click and isn't
  // persisted here.
  const [activeView] = useState<"conversations" | "goals" | "pinned">(
    "conversations",
  );

  const activeGoals = useMemo(
    () => goals.filter((g) => !TERMINAL_STATUSES.has(g.status)),
    [goals],
  );

  const activeGoal = useMemo(
    () =>
      activeGoals.find((g) => g.id === activeGoalId) ?? activeGoals[0] ?? null,
    [activeGoals, activeGoalId],
  );

  const userInitial = userEmail?.charAt(0).toUpperCase() ?? "U";

  return (
    <aside
      className="sika-chat-sidebar"
      data-testid="sika-chat-sidebar"
      data-collapsed={collapsed || undefined}
      data-mobile-open={mobileOpen || undefined}
      aria-label="SIKA chat navigation"
    >
      {/* Brand */}
      <div className="scs-brand">
        <div className="scs-brand-mark" aria-hidden>
          S
        </div>
        <div className="scs-brand-name">SIKA</div>
        <button
          type="button"
          className="scs-collapse-btn"
          onClick={onToggleCollapse}
          data-testid="scs-collapse-btn"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <Icon name={collapsed ? "chev-right" : "chev-left"} size={14} />
        </button>
      </div>

      {/* Scrollable nav body */}
      <div className="scs-scroll">
        {/* Top nav */}
        <button
          type="button"
          className="scs-nav-row"
          onClick={() => {
            onNewConversation();
            onCloseMobile?.();
          }}
          data-testid="scs-nav-new"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="plus" size={16} />
          </span>
          <span className="scs-nav-label">New conversation</span>
        </button>

        <button
          type="button"
          className="scs-nav-row"
          aria-disabled="true"
          disabled
          data-testid="scs-nav-search"
          title="Search — coming soon"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="search" size={16} />
          </span>
          <span className="scs-nav-label">Search</span>
          <span className="scs-nav-kbd">⌘K</span>
        </button>

        <button
          type="button"
          className="scs-nav-row"
          data-active={activeView === "conversations" || undefined}
          data-testid="scs-nav-conversations"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="bubble" size={16} />
          </span>
          <span className="scs-nav-label">Conversations</span>
        </button>

        <Link
          to="/sika"
          className="scs-nav-row"
          data-testid="scs-nav-goals"
          onClick={onCloseMobile}
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="target" size={16} />
          </span>
          <span className="scs-nav-label">Goals</span>
        </Link>

        <Link
          to={activeGoal ? `/sika?goal=${encodeURIComponent(activeGoal.id)}` : "/sika"}
          className="scs-nav-row"
          data-testid="scs-nav-active-plan"
          onClick={onCloseMobile}
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="chart" size={16} />
          </span>
          <span className="scs-nav-label">Active plan</span>
        </Link>

        <Link
          to="/vault"
          className="scs-nav-row"
          data-testid="scs-nav-vault"
          onClick={onCloseMobile}
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="shield" size={16} />
          </span>
          <span className="scs-nav-label">Vault</span>
        </Link>

        <button
          type="button"
          className="scs-nav-row"
          aria-disabled="true"
          disabled
          data-testid="scs-nav-pinned"
          title="Pinned — coming soon"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="star" size={16} />
          </span>
          <span className="scs-nav-label">Pinned</span>
        </button>

        {/* Goals section */}
        <div className="scs-section-head">Goals</div>
        {activeGoals.length === 0 ? (
          <div
            style={{
              padding: "6px 10px",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
            }}
            data-testid="scs-goals-empty"
          >
            No active goals
          </div>
        ) : (
          activeGoals.map((g) => {
            const active = g.id === activeGoalId;
            return (
              <button
                key={g.id}
                type="button"
                className="scs-goal-row"
                data-active={active || undefined}
                data-testid={`scs-goal-${g.id}`}
                onClick={() => {
                  onSelectGoal(g.id);
                  onCloseMobile?.();
                }}
                title={g.title}
              >
                <span className="scs-goal-dot" aria-hidden>
                  {active ? "◉" : "○"}
                </span>
                <span className="scs-goal-title">{g.title}</span>
                <span className="scs-goal-meta">{goalMeta(g)}</span>
              </button>
            );
          })
        )}

        {/* Recent conversations — reuses existing rail so we don't
            re-implement session list / time grouping. While collapsed
            we hide its labels via CSS; the rail itself stays mounted
            so its session state isn't lost. */}
        <div className="scs-section-head">Recent</div>
        <div className="scs-rail-wrap" data-testid="scs-recent-wrap">
          <SikaHistoryRail
            activeSessionId={activeSessionId}
            onSelectSession={(sid, gid) => {
              onSelectSession(sid, gid);
              onCloseMobile?.();
            }}
            onNewConversation={() => {
              onNewConversation();
              onCloseMobile?.();
            }}
          />
        </div>
      </div>

      {/* User chip */}
      <div className="scs-user-chip" data-testid="scs-user-chip">
        <div className="scs-user-avatar" aria-hidden>
          {userInitial}
        </div>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="scs-user-name">{userEmail ?? "Guest"}</div>
          <div className="scs-user-tier" data-tier={pro ? "pro" : "free"}>
            {pro ? "SIKA PRO" : "SIKA"}
          </div>
        </div>
      </div>
    </aside>
  );
}
